import { AxiosInstance } from "axios";
import type {
  OrgSelectionApi,
  SelectOrgRequest,
  SourceOrg,
} from "../source-settings/OrgSelection.types";
import type {
  CdsAttribute,
  CdsEntityDefinition,
  CdsStatusOption,
} from "./CDSSourceSettings.types";

export class CdsSourceSettingsApi implements OrgSelectionApi {
  constructor(private readonly client: AxiosInstance) {}

  /**
   * Environments (organisations) the connection's discovery token can see.
   * Only meaningful between the first OAuth leg and org selection.
   */
  async getOrgs(boardId: string, connectionId: string): Promise<SourceOrg[]> {
    const response = await this.client.get<SourceOrg[]>(
      `/api/boards/${encodeURIComponent(boardId)}/connections/${encodeURIComponent(connectionId)}/cds/orgs`,
    );
    return response.data;
  }

  /**
   * Save the chosen environment against the connection.
   * Returns the provider auth URL for the second, environment-scoped OAuth leg.
   */
  async selectOrg(
    boardId: string,
    connectionId: string,
    request: SelectOrgRequest,
  ): Promise<{ authUrl: string }> {
    const response = await this.client.post<{ authUrl: string }>(
      `/api/boards/${encodeURIComponent(boardId)}/connections/${encodeURIComponent(connectionId)}/cds/select-org`,
      request,
    );
    return response.data;
  }

  async getEntityDefinitions(
    boardId: string,
    connectionId: string,
  ): Promise<CdsEntityDefinition[]> {
    const response = await this.client.get<CdsEntityDefinition[]>(
      `/api/boards/${encodeURIComponent(boardId)}/connections/${encodeURIComponent(connectionId)}/cds/entitydefinitions`,
    );
    return response.data;
  }

  async getStatusOptions(
    boardId: string,
    connectionId: string,
    entityDefinitionId: string,
  ): Promise<CdsStatusOption[]> {
    const response = await this.client.get<CdsStatusOption[]>(
      `/api/boards/${encodeURIComponent(boardId)}/connections/${encodeURIComponent(connectionId)}/cds/entitydefinitions/${encodeURIComponent(entityDefinitionId)}/statusoptions`,
    );
    return response.data;
  }

  async getDateAttributes(
    boardId: string,
    connectionId: string,
    entityDefinitionId: string,
  ): Promise<CdsAttribute[]> {
    const response = await this.client.get<CdsAttribute[]>(
      `/api/boards/${encodeURIComponent(boardId)}/connections/${encodeURIComponent(connectionId)}/cds/entitydefinitions/${encodeURIComponent(entityDefinitionId)}/dateattributes`,
    );
    return response.data;
  }

  async getSystemUserAttributes(
    boardId: string,
    connectionId: string,
    entityDefinitionId: string,
  ): Promise<CdsAttribute[]> {
    const response = await this.client.get<CdsAttribute[]>(
      `/api/boards/${encodeURIComponent(boardId)}/connections/${encodeURIComponent(connectionId)}/cds/entitydefinitions/${encodeURIComponent(entityDefinitionId)}/systemuserattributes`,
    );
    return response.data;
  }

  async getAttributes(
    boardId: string,
    connectionId: string,
    entityDefinitionId: string,
  ): Promise<CdsAttribute[]> {
    const response = await this.client.get<CdsAttribute[]>(
      `/api/boards/${encodeURIComponent(boardId)}/connections/${encodeURIComponent(connectionId)}/cds/entitydefinitions/${encodeURIComponent(entityDefinitionId)}/attributes`,
    );
    return response.data;
  }
}
